import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import {
  AdvancedBookingSettings,
  CheckInSettings,
  CheckinAndMaxAdvancedBooking,
  MetaData
} from "../../Login/typings/login.types";
import { CheckinPolicyGroup } from "../CompanyCard/CompanyCard.types";

type VisitorCheckinPersist = Pick<
  CheckinAndMaxAdvancedBooking,
  "checkinBufferTime" | "geoLocationRequired" | "checkinWhenOpeningDoor" | "bookingType"
>;

/** Normalizes legacy visitor check-in fields when loading meta; visitor check-in is not saved in checkinSettings. */
export function normalizeVisitorCheckinSetting<T extends VisitorCheckinPersist>(setting: T): T {
  return {
    ...setting,
    geoLocationRequired: false,
    checkinWhenOpeningDoor: false
  };
}

export const displayValueForAllowedNoBookingValue = (
  newValue: number,
  currentValue: number
): number => {
  if (Number.isInteger(newValue)) return newValue;

  return currentValue;
};

export const getCheckInBufferTime = (checkInPolicy: boolean): number => {
  if (checkInPolicy) return 15;
  return -1;
};

export const initialCheckinSettings = [
  {
    checkinBufferTime: 15, // -1
    geoLocationRequired: false,
    checkinWhenOpeningDoor: false,
    bookingType: BookingType.WORKPLACE
  },
  {
    checkinBufferTime: 15, // -1
    geoLocationRequired: false,
    checkinWhenOpeningDoor: false,
    bookingType: BookingType.PARKINGPLACE
  },
  {
    checkinBufferTime: 15, // -1
    geoLocationRequired: false,
    checkinWhenOpeningDoor: false,
    bookingType: BookingType.ELECTRICCHARGINGSTATIONPLACE
  },
  {
    checkinBufferTime: 15, // -1
    geoLocationRequired: false,
    checkinWhenOpeningDoor: false,
    bookingType: BookingType.CONFERENCEZONE
  },
  {
    checkinBufferTime: 15, // -1
    geoLocationRequired: false,
    checkinWhenOpeningDoor: false,
    bookingType: BookingType.VEHICLE
  },
  {
    checkinBufferTime: 15, // -1
    geoLocationRequired: false,
    checkinWhenOpeningDoor: false,
    bookingType: BookingType.VISITOR
  }
];

export function updateCheckInSettings(
  prevData: MetaData,
  selectedType: BookingType,
  opt: CheckinAndMaxAdvancedBooking
) {
  // init empty checkin settings
  if (!prevData.checkinSettings.length) {
    prevData.checkinSettings = initialCheckinSettings;
  }

  const checkinIndex = prevData.checkinSettings.findIndex(s => s.bookingType === selectedType);

  if (checkinIndex < 0) return prevData;
  if (opt.checkinBufferTime)
    prevData.checkinSettings[checkinIndex].checkinBufferTime = opt.checkinBufferTime as number;
  if (opt.geoLocationRequired)
    prevData.checkinSettings[checkinIndex].geoLocationRequired = opt.geoLocationRequired;
  if (opt.checkinWhenOpeningDoor)
    prevData.checkinSettings[checkinIndex].checkinWhenOpeningDoor = opt.checkinWhenOpeningDoor;

  return prevData;
}

export function initialCheckinEnableGrp(checkinSettings: CheckInSettings[]) {
  return {
    workplace:
      (checkinSettings?.find(s => s.bookingType === BookingType.WORKPLACE) &&
        checkinSettings?.find(s => s.bookingType === BookingType.WORKPLACE)?.checkinBufferTime !==
          -1) ||
      false,
    parkingplace:
      (checkinSettings?.find(s => s.bookingType === BookingType.PARKINGPLACE) &&
        checkinSettings?.find(s => s.bookingType === BookingType.PARKINGPLACE)
          ?.checkinBufferTime !== -1) ||
      false,
    electricchargingplace:
      (checkinSettings?.find(s => s.bookingType === BookingType.ELECTRICCHARGINGSTATIONPLACE) &&
        checkinSettings?.find(s => s.bookingType === BookingType.ELECTRICCHARGINGSTATIONPLACE)
          ?.checkinBufferTime !== -1) ||
      false,
    conferencezone:
      (checkinSettings?.find(s => s.bookingType === BookingType.CONFERENCEZONE) &&
        checkinSettings?.find(s => s.bookingType === BookingType.CONFERENCEZONE)
          ?.checkinBufferTime !== -1) ||
      false,
    vehicle:
      (checkinSettings?.find(s => s.bookingType === BookingType.VEHICLE) &&
        checkinSettings?.find(s => s.bookingType === BookingType.VEHICLE)?.checkinBufferTime !==
          -1) ||
      false,
    visitor:
      (checkinSettings?.find(s => s.bookingType === BookingType.VISITOR) &&
        checkinSettings?.find(s => s.bookingType === BookingType.VISITOR)?.checkinBufferTime !==
          -1) ||
      false
  };
}

export function filterEnabledPolicy({
  checkinEnabledGrp,
  wpSetting,
  parkPlSetting,
  eChargePlSetting,
  confZnSetting,
  vehicleSetting
}: {
  checkinEnabledGrp: CheckinPolicyGroup;
  wpSetting: CheckinAndMaxAdvancedBooking;
  parkPlSetting: CheckinAndMaxAdvancedBooking;
  eChargePlSetting: CheckinAndMaxAdvancedBooking;
  confZnSetting: CheckinAndMaxAdvancedBooking;
  vehicleSetting: CheckinAndMaxAdvancedBooking;
  visitorSetting: CheckinAndMaxAdvancedBooking;
}) {
  // derive checkin setting only from Checkin And Max Advanced Booking settings
  const wpCheckinSetting = {
    checkinBufferTime: wpSetting.checkinBufferTime,
    geoLocationRequired: wpSetting.geoLocationRequired,
    checkinWhenOpeningDoor: wpSetting.checkinWhenOpeningDoor,
    bookingType: wpSetting.bookingType
  };
  const parkPlCheckinSetting = {
    checkinBufferTime: parkPlSetting.checkinBufferTime,
    geoLocationRequired: parkPlSetting.geoLocationRequired,
    checkinWhenOpeningDoor: parkPlSetting.checkinWhenOpeningDoor,
    bookingType: parkPlSetting.bookingType
  };
  const eChargePlCheckinSetting = {
    checkinBufferTime: eChargePlSetting.checkinBufferTime,
    geoLocationRequired: eChargePlSetting.geoLocationRequired,
    checkinWhenOpeningDoor: eChargePlSetting.checkinWhenOpeningDoor,
    bookingType: eChargePlSetting.bookingType
  };
  const confZnCheckinSetting = {
    checkinBufferTime: confZnSetting.checkinBufferTime,
    geoLocationRequired: confZnSetting.geoLocationRequired,
    checkinWhenOpeningDoor: confZnSetting.checkinWhenOpeningDoor,
    bookingType: confZnSetting.bookingType
  };
  const vehicleCheckinSetting = {
    checkinBufferTime: vehicleSetting.checkinBufferTime,
    geoLocationRequired: vehicleSetting.geoLocationRequired,
    checkinWhenOpeningDoor: vehicleSetting.checkinWhenOpeningDoor,
    bookingType: vehicleSetting.bookingType
  };
  let newCheckinSettings = [
    wpCheckinSetting,
    parkPlCheckinSetting,
    eChargePlCheckinSetting,
    confZnCheckinSetting,
    vehicleCheckinSetting
  ];

  if (!checkinEnabledGrp.workplace) {
    newCheckinSettings = newCheckinSettings.filter(n => n.bookingType !== BookingType.WORKPLACE);
  }
  if (!checkinEnabledGrp.parkingplace) {
    newCheckinSettings = newCheckinSettings.filter(n => n.bookingType !== BookingType.PARKINGPLACE);
  }
  if (!checkinEnabledGrp.electricchargingplace) {
    newCheckinSettings = newCheckinSettings.filter(
      n => n.bookingType !== BookingType.ELECTRICCHARGINGSTATIONPLACE
    );
  }
  if (!checkinEnabledGrp.conferencezone) {
    newCheckinSettings = newCheckinSettings.filter(
      n => n.bookingType !== BookingType.CONFERENCEZONE
    );
  }
  if (!checkinEnabledGrp.vehicle) {
    newCheckinSettings = newCheckinSettings.filter(n => n.bookingType !== BookingType.VEHICLE);
  }
  return newCheckinSettings.filter(n => n.bookingType !== BookingType.VISITOR);
}

export function generateAdvancedBookingSettings(
  wpSetting: CheckinAndMaxAdvancedBooking,
  parkPlSetting: CheckinAndMaxAdvancedBooking,
  eChargePlSetting: CheckinAndMaxAdvancedBooking,
  confZnSetting: CheckinAndMaxAdvancedBooking,
  vehicleSetting: CheckinAndMaxAdvancedBooking,
  visitorSetting: CheckinAndMaxAdvancedBooking
): Array<AdvancedBookingSettings> {
  const transformSetting = (setting: CheckinAndMaxAdvancedBooking): AdvancedBookingSettings => {
    return {
      bookingType: setting.bookingType,
      maxWeeksInAdvance: setting.maxWeeksInAdvance,
      ...(setting.weekday ? { weekday: setting.weekday } : {}),
      ...(setting.time ? { time: setting.time } : {}),
      ...(setting.allowedBookingDaysInAdvance
        ? { allowedBookingDaysInAdvance: setting.allowedBookingDaysInAdvance }
        : {})
    };
  };

  return [
    transformSetting(wpSetting),
    transformSetting(parkPlSetting),
    transformSetting(eChargePlSetting),
    transformSetting(confZnSetting),
    transformSetting(vehicleSetting),
    transformSetting(visitorSetting)
  ];
}

export function initializeCheckinSettingAdvancedBookingTime(
  checkinSettings: CheckInSettings[],
  advancedBookingSettings: AdvancedBookingSettings[] | undefined
) {
  const wpCheckin =
    checkinSettings?.find(c => c.bookingType === BookingType.WORKPLACE) ||
    initialCheckinSettings[0];
  const wpAdvancedBooking = advancedBookingSettings?.find(
    ad => ad.bookingType === BookingType.WORKPLACE
  );

  const parkCheckin =
    checkinSettings?.find(c => c.bookingType === BookingType.PARKINGPLACE) ||
    initialCheckinSettings[1];
  const parkAdvancedBooking = advancedBookingSettings?.find(
    ad => ad.bookingType === BookingType.PARKINGPLACE
  );

  const elecCheckin =
    checkinSettings?.find(c => c.bookingType === BookingType.ELECTRICCHARGINGSTATIONPLACE) ||
    initialCheckinSettings[2];
  const elecAdvancedBooking = advancedBookingSettings?.find(
    ad => ad.bookingType === BookingType.ELECTRICCHARGINGSTATIONPLACE
  );

  const confCheckin =
    checkinSettings?.find(c => c.bookingType === BookingType.CONFERENCEZONE) ||
    initialCheckinSettings[3];
  const confAdvancedBooking = advancedBookingSettings?.find(
    ad => ad.bookingType === BookingType.CONFERENCEZONE
  );

  const vehicleCheckin =
    checkinSettings?.find(c => c.bookingType === BookingType.VEHICLE) || initialCheckinSettings[4];
  const vehicleAdvancedBooking = advancedBookingSettings?.find(
    ad => ad.bookingType === BookingType.VEHICLE
  );

  const visitorCheckin =
    checkinSettings?.find(c => c.bookingType === BookingType.VISITOR) || initialCheckinSettings[5];
  const visitorAdvancedBooking = advancedBookingSettings?.find(
    ad => ad.bookingType === BookingType.VISITOR
  );

  // make initial Setting
  const initialWpSetting: CheckinAndMaxAdvancedBooking = {
    bookingType: BookingType.WORKPLACE,
    checkinBufferTime: wpCheckin.checkinBufferTime,
    geoLocationRequired: wpCheckin.geoLocationRequired,
    checkinWhenOpeningDoor: wpCheckin.checkinWhenOpeningDoor,
    maxWeeksInAdvance: wpAdvancedBooking?.maxWeeksInAdvance || 6,
    weekday: wpAdvancedBooking?.weekday ?? null,
    time: wpAdvancedBooking?.time ?? "",
    allowedBookingDaysInAdvance: wpAdvancedBooking?.allowedBookingDaysInAdvance ?? null
  };
  const initialParkSetting: CheckinAndMaxAdvancedBooking = {
    bookingType: BookingType.PARKINGPLACE,
    checkinBufferTime: parkCheckin.checkinBufferTime,
    geoLocationRequired: parkCheckin.geoLocationRequired,
    checkinWhenOpeningDoor: parkCheckin.checkinWhenOpeningDoor,
    maxWeeksInAdvance: parkAdvancedBooking?.maxWeeksInAdvance || 6,
    weekday: parkAdvancedBooking?.weekday ?? null,
    time: parkAdvancedBooking?.time ?? "",
    allowedBookingDaysInAdvance: parkAdvancedBooking?.allowedBookingDaysInAdvance ?? null
  };
  const initialElecSetting: CheckinAndMaxAdvancedBooking = {
    bookingType: BookingType.ELECTRICCHARGINGSTATIONPLACE,
    checkinBufferTime: elecCheckin.checkinBufferTime,
    geoLocationRequired: elecCheckin.geoLocationRequired,
    checkinWhenOpeningDoor: elecCheckin.checkinWhenOpeningDoor,
    maxWeeksInAdvance: elecAdvancedBooking?.maxWeeksInAdvance || 6,
    weekday: elecAdvancedBooking?.weekday ?? null,
    time: elecAdvancedBooking?.time ?? "",
    allowedBookingDaysInAdvance: elecAdvancedBooking?.allowedBookingDaysInAdvance ?? null
  };
  const initialConfSetting: CheckinAndMaxAdvancedBooking = {
    bookingType: BookingType.CONFERENCEZONE,
    checkinBufferTime: confCheckin.checkinBufferTime,
    geoLocationRequired: confCheckin.geoLocationRequired,
    checkinWhenOpeningDoor: confCheckin.checkinWhenOpeningDoor,
    maxWeeksInAdvance: confAdvancedBooking?.maxWeeksInAdvance || 6,
    weekday: confAdvancedBooking?.weekday ?? null,
    time: confAdvancedBooking?.time ?? "",
    allowedBookingDaysInAdvance: confAdvancedBooking?.allowedBookingDaysInAdvance ?? null
  };
  const initialVehicleSetting: CheckinAndMaxAdvancedBooking = {
    bookingType: BookingType.VEHICLE,
    checkinBufferTime: vehicleCheckin.checkinBufferTime,
    geoLocationRequired: vehicleCheckin.geoLocationRequired,
    checkinWhenOpeningDoor: vehicleCheckin.checkinWhenOpeningDoor,
    maxWeeksInAdvance: vehicleAdvancedBooking?.maxWeeksInAdvance || 6,
    weekday: vehicleAdvancedBooking?.weekday ?? null,
    time: vehicleAdvancedBooking?.time ?? "",
    allowedBookingDaysInAdvance: vehicleAdvancedBooking?.allowedBookingDaysInAdvance ?? null
  };
  const initialVisitorSetting: CheckinAndMaxAdvancedBooking = normalizeVisitorCheckinSetting({
    bookingType: BookingType.VISITOR,
    checkinBufferTime: visitorCheckin.checkinBufferTime,
    geoLocationRequired: visitorCheckin.geoLocationRequired,
    checkinWhenOpeningDoor: visitorCheckin.checkinWhenOpeningDoor,
    maxWeeksInAdvance: visitorAdvancedBooking?.maxWeeksInAdvance || 6,
    weekday: visitorAdvancedBooking?.weekday ?? null,
    time: visitorAdvancedBooking?.time ?? "",
    allowedBookingDaysInAdvance: visitorAdvancedBooking?.allowedBookingDaysInAdvance ?? null
  });

  return {
    initialWpSetting,
    initialParkSetting,
    initialElecSetting,
    initialConfSetting,
    initialVehicleSetting,
    initialVisitorSetting
  };
}
